import { useEffect, useRef, useState } from "react";

import { Icon } from "@blueprintjs/core";
import uniq from "lodash/uniq";
import {
  Channel as ChannelType,
  DefaultGenerics,
  StreamChat,
} from "stream-chat";
import {
  Attachment,
  AttachmentProps,
  Channel,
  Chat,
  MessageInput,
  MessageList,
  Thread,
  Window,
} from "stream-chat-react";
import { EmojiPicker } from "stream-chat-react/emojis";

import { Box, Flex } from "@/components/layout/flexbox";
import { getUser, isImpersonating } from "@/components/session/JwtTokenManager";
import { SUPPORT_EMAIL } from "@/css/constants";

import "stream-chat-react/dist/css/v2/index.css";

import { ChatContainer } from "./ChatContainer";
import { getChatClient } from "./ChatContext";
import { ChatLoader } from "./ChatLoader";
import { CustomMessageTimestamp } from "./CustomMessageTimestamp";

// Create a custom message list component to handle auto-scrolling
function AutoScrollMessageList({
  customerId,
  isImpersonating,
}: {
  customerId: string;
  isImpersonating: boolean;
}) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const messageActions =
    customerId || isImpersonating ? [] : ["delete", "edit", "react"];
  return (
    <>
      <MessageList messageActions={messageActions} />
      <div ref={messagesEndRef} />
    </>
  );
}

interface ChatComponentProps {
  channelId: string;
  height?: number;
  customerId?: string;
  requiredMemberIds?: string[];
  frozenMessage?: string;
}

export function ChatComponent({
  channelId,
  height,
  customerId,
  requiredMemberIds = [],
  frozenMessage = "Sending new messages here is disabled",
}: ChatComponentProps) {
  const mountedRef = useRef<boolean>(false);
  const [chatClient, setChatClient] =
    useState<StreamChat<DefaultGenerics> | null>(null);
  const [channel, setChannel] = useState<ChannelType<DefaultGenerics> | null>(
    null,
  );
  const [error, setError] = useState<React.ReactNode | null>(null);
  const user = getUser();

  async function connectToChannel() {
    const chatClient = getChatClient();
    const chatChannel = chatClient.channel("messaging", channelId);
    await chatChannel.watch();
    if (mountedRef.current) {
      setChatClient(chatClient);
      setChannel(chatChannel);
    }
  }

  useEffect(() => {
    // Try to add the required members. First, check if the current
    // user has admin privileges. If the user doesn't have
    // permission to add members, stream will error. We are catching
    // the permission denied error and ignoring it.
    let isSubscribed = true;
    if (channel && chatClient) {
      async function tryToAddMembers() {
        if (!isSubscribed) return;

        const response = await chatClient?.queryUsers({ id: user?.id });
        if (response?.users[0]?.role === "admin") {
          channel
            ?.addMembers(
              uniq(requiredMemberIds.concat([user?.id ?? ""])).map(
                (x) => "" + x,
              ),
            )
            .catch(() => null);
        }
      }
      tryToAddMembers();
    }
    return () => {
      isSubscribed = false;
    };
  }, [requiredMemberIds, channel, chatClient]);

  useEffect(() => {
    mountedRef.current = true;
    connectToChannel().catch((e) => {
      if (e.message.includes("is not allowed to access")) {
        setError(
          <>
            You don't have access to this chat room.
            <br />
            Please <a href={`mailto:${SUPPORT_EMAIL}`}>contact support</a> if
            you believe this is an error.
          </>,
        );
      } else {
        setError(
          <>
            An error occured while trying to load this chat room.
            <br />
            Please <a href={`mailto:${SUPPORT_EMAIL}`}>contact support</a> if it
            continues happening.
          </>,
        );
      }
    });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isFrozen = channel?.data?.frozen;

  function CustomAttachment(props: AttachmentProps) {
    return <Attachment {...props} />;
  }

  function CustomFileUploadIcon() {
    return <Icon icon="paperclip" size={24} />;
  }

  function CustomLoadingIndicator() {
    return <ChatLoader position="relative" />;
  }

  return (
    <>
      <Box>
        {channel ? (
          <ChatContainer height={height}>
            <Chat client={getChatClient()} theme={"messaging"}>
              <Channel
                channel={channel}
                Attachment={CustomAttachment}
                LoadingIndicator={CustomLoadingIndicator}
                EmojiPicker={EmojiPicker}
                MessageTimestamp={CustomMessageTimestamp}
                AttachmentSelectorInitiationButtonContents={
                  CustomFileUploadIcon
                }
              >
                <Window>
                  <AutoScrollMessageList
                    customerId={customerId ?? ""}
                    isImpersonating={isImpersonating()}
                  />
                  {isFrozen ? (
                    <Box py={3} px={4} className="bts">
                      <i>{frozenMessage}</i>
                    </Box>
                  ) : (
                    <MessageInput grow={true} />
                  )}
                </Window>
                <Thread
                  additionalMessageInputProps={{
                    grow: true,
                  }}
                />
              </Channel>
            </Chat>
          </ChatContainer>
        ) : error ? (
          <Flex
            px={4}
            pt={100}
            textAlign="center"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.75)",
            }}
            justifyContent="center"
          >
            <Box>{error}</Box>
          </Flex>
        ) : (
          <ChatLoader position="relative" />
        )}
      </Box>
    </>
  );
}
